// This file is meant to hold utility methods that don't have any Blink-specific logic,
// so they can be used anywhere

import {isMobileDevice} from "../stem-core/src/base/Device";
import {MOBILE_DEVICE_SCREEN_WIDTH_LIMIT, SMALL_SCREEN_WIDTH_LIMIT} from "./Constants";

export * from "../stem-core/src/base/Utils";

// BlinkGlobal can be used to prevent circular dependencies, and where we don't want to pollute window/self.
export const BlinkGlobal = {};

export function isDeepEqual(obj1, obj2) {
    // Different types means different variables.
    if (typeof obj1 !== typeof obj2) {
        return false;
    }
    if (typeof obj1 === "object") {
        // If one of them is null or undefined, the equality check is trivial.
        if (obj1 == null || obj2 == null) {
            return obj1 === obj2;
        }
        // A non-null object has entries. If the entry count differs, they are different
        if (Object.keys(obj1).length !== Object.keys(obj2).length) {
            return false;
        }
        // If the entry count is the same, it's enough to check that the second object has each of the first's entries.
        for (const key of Object.keys(obj1)) {
            if (!isDeepEqual(obj1[key], obj2[key])) {
                return false;
            }
        }
        // The objects have the same entries. That means they are equal.
        return true;
    }
    // Not an object, so this is trivial.
    return obj1 === obj2;
}

export function getUrlQueryString() {
    return window.location.search;
}

export function getQueryParam(param) {
    const urlSearch = new URLSearchParams(getUrlQueryString());
    try {
        return JSON.parse(urlSearch.get(param));
    } catch (e1) {
        try {
            return JSON.parse(decodeURIComponent(urlSearch.get(param)));
        } catch (e2) {
            try {
                return decodeURIComponent(urlSearch.get(param));
            } catch (e3) {
                return null;
            }
        }
    }
}

export function commify(number) {
    let result = "";
    const numberString = number.toString();
    for (let i = 0; i < numberString.length; i += 1) {
        if (i && (i - numberString.length) % 3 === 0) {
            result += ",";
        }
        result += numberString[i];
    }
    return result;
}

export function formatQueryParam(param) {
    try {
        return encodeURIComponent(JSON.stringify(param));
    } catch (e1) {
        try {
            return encodeURIComponent(param);
        } catch (e2) {
            return "";
        }
    }
}

export function generateUniqueId() {
    // generate UUID v4
    const seq = Date.now().toString(16) + parseInt(Math.random() * 1e18).toString(16) + parseInt(Math.random() * 1e18).toString(16);

    return [seq.substr(0, 8), seq.substr(8, 4), `4${seq.substr(12, 3)}`, `8${seq.substr(15, 3)}`, seq.substr(18, 12)].join("-");
}

export function getWindowWidth() {
    if (BlinkGlobal.iFrameState?.screen && isMobileDevice()) {
        return BlinkGlobal.iFrameState.screen.width;
    }
    if (window.screen && isMobileDevice()) {
        return window.screen.width;
    }
    return window.innerWidth;
}

export function getWindowHeight() {
    return document.documentElement.clientHeight;
}

export function isSmallScreen() {
    if (BlinkGlobal.iFrameState?.screen?.isEmulatorScreen) {
        return BlinkGlobal.iFrameState.screen.width <= MOBILE_DEVICE_SCREEN_WIDTH_LIMIT;
    }
    if (window.screen && isMobileDevice()) {
        return getWindowWidth() <= MOBILE_DEVICE_SCREEN_WIDTH_LIMIT;
    }
    return getWindowWidth() <= SMALL_SCREEN_WIDTH_LIMIT;
}

export function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function getDocumentHiddenInfo() {
    if (document.hidden) {
        return {
            hidden: true,
            event: "visibilitychange"
        };
    } else if (document.msHidden) {
        return {
            hidden: true,
            event: "msvisibilitychange"
        };
    } else if (document.webkitHidden) {
        return {
            hidden: true,
            event: "webkitvisibilitychange"
        };
    }
    return {hidden: false};
}

export function executeWhenDocumentVisible(callback) {
    const hiddenInfo = getDocumentHiddenInfo();
    if (!hiddenInfo.hidden) {
        callback();
        return {removeHandler: null};
    }
    const handlerFunction = () => {
        setTimeout(callback, 300);
        removeHandler();
    };
    const removeHandler = () => document.removeEventListener(hiddenInfo.event, handlerFunction);
    document.addEventListener(hiddenInfo.event, handlerFunction);
    return {removeHandler};
}

export function openWindowWithOpener(url="", target="_blank") {
    return window.open(url, target, "opener");
}
